import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { isURL } from 'validator'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

export interface RequestOptions {
  signal?: AbortSignal
}

export interface Variables extends RequestOptions {
  url: string
}

const WEBSITE_BLOCKED_ERROR_MSG = 'This website blocks automated access. Please try another site.'

export class WebsiteBlockedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'WebsiteBlockedError'
  }
}

export type Options = Omit<UseMutationOptions<string, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export const useCreateScreenshot = (options?: Options) => {
  return useMutation<string, unknown, Variables>(
    'createScreenshot',
    async ({ url, signal }) => {
      if (
        !isURL(url, { require_tld: true, require_protocol: true }) &&
        isURL(`https://${url}`, { require_tld: true, require_protocol: true })
      ) {
        url = `https://${url}`
      }

      const response = await sendToApi<string>(`/screenshot-url`, {
        method: 'POST',
        signal,
        body: {
          url,
        },
      })

      // Do not handle cloudflare 520 error, it's not a blocked website
      if (response.type === 'error' && response.status === 408) {
        showErrorMessageToast(WEBSITE_BLOCKED_ERROR_MSG)
        throw new WebsiteBlockedError(WEBSITE_BLOCKED_ERROR_MSG)
      } else if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }
      return response.data
    },
    {
      ...options,
    },
  )
}
